import React, { useState, useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  Image,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { useDispatch } from "react-redux";

import colors from "@constants/color";
import metrics from "@constants/metrics";
import { getCohortById, getAllLeaderCustomers } from "@services/api";

import SearchBar from "@components/SearchBar";
import Text from "@components/AppText";
import BackArrow from "@assets/svg/BackArrow";
import NoCustomerSegment from "@assets/svg/NoCustomerSegment";
import Button from "@components/Button";
import { useDebounce } from "@utils/debounce";

import analytics from "@services/analytics";
import { CUSTOMER_PAGE, ORDER_FLOW_EVENTS } from "@constants/analyticsEventsCT";
import t from "@constants/localization";
import { CustomerListItem } from "./CustomerListItem";

const PATH = "screens/YourStore/CustomerSegmentList";

const CustomerSegmentList = (props) => {
  const {
    navigation,
    route: { params = {} },
  } = props;
  const dispatch = useDispatch();

  const offset = useRef(0);
  const limit = useRef(10);

  const loadingRef = useRef(false);

  const [searchQuery, setSearchQuery] = useState();
  const [customerList, setCustomerList] = useState({});
  const [cohort, setCohort] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const debounceSearchTerm = useDebounce(searchQuery, 300);

  useEffect(() => {
    getCohortById(params.cohort_id)
      .then((response) => {
        setCohort(response.data);
        setIsPageLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    const willFocusSubscription = navigation.addListener("focus", () => {
      offset.current = 0;
      getAllCustomer();
    });
    return willFocusSubscription;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    offset.current = 0;
    getAllCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchTerm]);

  const getAllCustomer = (type = null) => {
    if (!type) {
      setIsLoading(true);
    }
    getAllLeaderCustomers({
      limit: limit.current,
      offset: offset.current,
      query: searchQuery && searchQuery.length > 0 ? searchQuery : "",
      cohortId: params.cohort_id,
    })
      .then((response) => {
        setIsLoading(false);
        loadingRef.current = false;
        if (type === "onscroll") {
          setCustomerList({
            ...response.data,
            data: [...customerList.data, ...response.data.data],
          });
        } else {
          setCustomerList(response.data);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.log("##", error);
      });
  };

  const onScrollCustomerList = (e) => {
    const { nativeEvent } = e;
    if (
      isCloseToBottom(nativeEvent) &&
      customerList &&
      customerList.data &&
      customerList.data.length >= 10 &&
      customerList.count > offset.current &&
      !loadingRef.current
    ) {
      console.log("on End Reach");
      offset.current += limit.current;
      loadingRef.current = true;
      getAllCustomer("onscroll");
    }
  };

  const isCloseToBottom = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }) => {
    const paddingToBottom = 20;
    return (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    );
  };
  const handleCustomer = (customer) => {
    analytics.trackEvent(CUSTOMER_PAGE.CX_PAGE_NAME_CLICKED, {
      cx_id: customer.user_id,
    });

    navigation.navigate("CustomerProfile", {
      user: customer,
      fetchProfile: true,
    });
  };

  const _renderCustomerList = ({ item }) => {
    return (
      <CustomerListItem
        isNew={item.is_new}
        connectedByCm={item.is_connected_by_cm}
        onPress={() => handleCustomer(item)}
        name={item.user_name}
        mobile={item.user_phone}
        appShare={item.app_share_link}
        user_id={item.user_id}
        cx_trend={item.cx_trend}
      />
    );
  };

  if (isPageLoading || Object.keys(cohort) > 5) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  } else {
    return (
      <View style={styles.container}>
        <LinearGradient
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          colors={cohort.bg_colors || ["white", "white"]}
          style={styles.headerContainer}
        >
          <View
            style={{
              width: "60%",
            }}
          >
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <BackArrow color={colors.white} />
            </TouchableOpacity>
            <View style={styles.header}>
              <View style={styles.row}>
                <Text bold fontType="head2" style={styles.colorWhite}>
                  {cohort.title}
                </Text>
              </View>
              <Text style={styles.subTitleText} fontType="cont1">
                {cohort.sub_title}
              </Text>
              <View style={[styles.row, styles.statContainer]}>
                <View style={styles.row}>
                  <Text bold style={styles.colorWhite}>
                    {cohort.num_cx}{" "}
                  </Text>
                  <Text fontType="content1" style={styles.colorWhite}>
                    {t(`${PATH}/customers`)}
                  </Text>
                </View>
                <View style={styles.dividerLine} />
                <View style={styles.row}>
                  <Text bold style={styles.colorWhite}>
                    ₹{cohort.sales}{" "}
                  </Text>
                  <Text fontType="content1" style={styles.colorWhite}>
                    {t(`${PATH}/earning`)}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <View style={styles.imageContainer}>
            <Image
              style={{ width: 120, height: 120 }}
              source={{
                uri: cohort.image,
              }}
              resizeMode="contain"
            />
          </View>
        </LinearGradient>
        {cohort.num_cx === 0 && (
          <View style={styles.notResultFoundContainer}>
            <NoCustomerSegment />
            <Text fontType="head3" style={styles.notFoundText}>
              {params.cohort_id === "new" && t(`${PATH}/new`)}
              {params.cohort_id === "champs" && t(`${PATH}/champs`)}
              {params.cohort_id === "churning" ||
                (params.cohort_id === "inactive" && t(`${PATH}/inactive`))}
            </Text>
            {params.cohort_id === "new" && (
              <View style={styles.addCustomerButton}>
                <Button
                  btnText={t(`${PATH}/addCustomer`)}
                  buttonWidth="100%"
                  onPress={() =>
                    navigation.navigate("Customer", { fromCustomer: true })
                  }
                />
              </View>
            )}
          </View>
        )}
        {cohort.num_cx > 0 && (
          <>
            <SearchBar
              customStyles={styles.searchContainer}
              query={searchQuery}
              placeholder={t(`${PATH}/searchCustomer`)}
              shouldShowIcon
              handleQueryChange={(value) => setSearchQuery(value)}
              onPressRightIcon={() => setSearchQuery("")}
            />
            {isLoading || Object.keys(cohort) > 5 ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : (
              <FlatList
                data={customerList.data}
                keyExtractor={(item) => item.id}
                showsVerticalScrollIndicator={false}
                renderItem={_renderCustomerList}
                initialNumToRender={20}
                onEndReachedThreshold={0.9}
                contentContainerStyle={styles.listContentContainer}
                onScroll={onScrollCustomerList}
                refreshControl={
                  <RefreshControl
                    style={styles.refreshControl}
                    refreshing={isLoading}
                    onRefresh={() => {
                      offset.current = 0;
                      getAllCustomer();
                    }}
                    colors={[colors.primary]}
                  />
                }
              />
            )}
          </>
        )}
      </View>
    );
  }
};

export default CustomerSegmentList;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignContent: "center",
    justifyContent: "center",
  },
  header: {
    padding: metrics.baseGutter,
  },
  headerContainer: {
    flexDirection: "row",
  },
  backButton: {
    height: 50,
    width: 50,
    justifyContent: "center",
    paddingLeft: metrics.baseGutter,
  },
  imageContainer: {
    width: "40%",
    alignItems: "center",
    justifyContent: "center",
  },
  searchContainer: {
    padding: metrics.baseGutter,
    backgroundColor: "#fff",
    // padding: ,
  },
  colorWhite: {
    color: "#fff",
  },
  row: {
    flexDirection: "row",
  },
  subTitleText: {
    opacity: 0.5,
  },
  dividerLine: {
    backgroundColor: "#fff",
    width: 1,
    marginHorizontal: 10,
    marginVertical: 5,
  },
  statContainer: {
    marginTop: 10,
  },
  notResultFoundContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  notFoundText: {
    textAlign: "center",
    marginTop: 20,
    marginHorizontal: 20,
  },
  listContentContainer: {
    paddingBottom: 100,
  },
  addCustomerButton: {
    width: "100%",
    paddingHorizontal: metrics.baseGutter,
    marginTop: 20,
  },
});
